import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeleteEntity, useUser } from '@work4all/data';

import { TimeTracking } from '@work4all/models/lib/Classes/TimeTracking.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import {
  canAddTimeTrackingForProjects,
  canEditTimeTrackingForOthers,
} from '@work4all/utils/lib/permissions';

import { ProjectTimeMaskInit } from '../../../containers/time-tracker/ProjectTimeMask';
import { usePermissionsRights } from '../../../hooks/use-permissions-rights';
import { DELETE_ENTITY_MAX_COUNT } from '../../data-tables/constants';
import { EntityTableLayoutV8Props } from '../../data-tables/table/EntityTableLayoutV8';

import { useOpenTimeTrackingMask } from './use-open-time-tracking-mask';

type Return = EntityTableLayoutV8Props['actions'];

export const useTimeTrackingMaskActions = (
  record: TimeTracking | undefined,
  initialData?: ProjectTimeMaskInit
): Return => {
  const user = useUser();
  const openTimeTrackingMask = useOpenTimeTrackingMask();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { isTicketLightUser } = usePermissionsRights();

  const [deleteEntity] = useDeleteEntity();

  const deleteHandler = useCallback(
    (ids: string[]) => {
      return deleteEntity({
        type: Entities.timeTracking,
        ids,
      });
    },
    [deleteEntity]
  );

  return useMemo<Return>(() => {
    const canEditAll = canEditTimeTrackingForOthers(user, undefined);

    const canAdd =
      !isTicketLightUser && (canAddTimeTrackingForProjects(user) || canEditAll);

    const canEdit = record && canAdd;

    return {
      add: {
        handler: () =>
          openTimeTrackingMask({
            mode: 'create',
            initialData,
          }),
        disabled: !canAdd,
      },
      edit: {
        handler: () => {
          if (record?.id) {
            openTimeTrackingMask({ id: record?.id, mode: 'edit' });
          } else {
            enqueueSnackbar(t('ALERTS.ERROR.UNKNOWN'), {
              variant: 'error',
            });
          }
        },
        disabled: !canEdit,
      },
      remove: {
        handler: deleteHandler,
        disabled: !canEdit,
        maxCount: DELETE_ENTITY_MAX_COUNT,
      },
    };
  }, [
    user,
    isTicketLightUser,
    record,
    deleteHandler,
    openTimeTrackingMask,
    initialData,
    enqueueSnackbar,
    t,
  ]);
};
